/********************************************************************************
 * Copyright (c) 2022 Cedalo GmbH
 *
 * All rights reserved. This program and the accompanying materials
 * are made available under the terms of the Eclipse Public License 2.0 which accompany this distribution.
 * and Eclipse Distribution License v1.0 which accompany this distribution.
 *
 * The Eclipse Public License is available at
 *   https://www.eclipse.org/legal/epl-2.0/
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
import JSG from '@cedalo/jsg-ui';
import CommandStack from './CommandStack';

const { InteractionHandler, SheetCommandFactory } = JSG;

const IGNORE_COMMANDS = [
	'command.SetGraphItemsCommand',
	// ignore selection commands
	'command.RemoveSelectionCommand',
	'command.SetSelectionCommand',
	'command.ChangeItemOrderCommand'
];
// inner commands which are not send to server
const TRIGGER_COMMANDS = [
	'command.AddItemCommand',
	//  'command.DeleteItemCommand',
	'command.InternalDeleteItemCommand',
	'command.PasteItemsCommand',
	'command.SetLayoutSectionCommand',
	'command.SetPlotDataCommand',
	'command.SetNameCommand',
	'command.SetTextCommand'
];

const getCommandName = (command) => {
	const name = command.name;
	return name || `command.${command.constructor.name}`;
};
const isServerCommand = (name) => name && name.startsWith('command.server');
const isShape = (item) => {
	while (item && !item.isCellsNode) item = item.getParent();
	return !!item;
};
const isShapeSelected = (selection = []) => !!selection.length && isShape(selection[0].getModel());
const isMessageBox = (item) => {
	while (item && !item.isMessageContainer) item = item.getParent();
	return !!item;
};
const getCurrentRedoCommand = (cmdstack) => cmdstack.redostack.at(-1);
const getCurrentUndoCommand = (cmdstack) => cmdstack.undostack.at(-1);

class AppInteractionHandler extends InteractionHandler {
	constructor(viewer, graph) {
		super(viewer);
		this.graph = graph;
		this.graph.context = {};
		this.commandStack = new CommandStack();
		this._doRepaint = true;
	}

	execute(command, completionFunction) {
		super.execute(command, completionFunction);
		if (this.updateShapesOnExecute(command)) {
			this.updateGraphItems();
		}
	}

	redo() {
		const doUpdate = this.updateShapesOnRedo(getCurrentRedoCommand(this.commandStack));
		super.redo();
		if (doUpdate) {
			this.updateGraphItems();
		}
	}
	undo() {
		const doUpdate = this.updateShapesOnUndo(getCurrentUndoCommand(this.commandStack));
		super.undo();
		if (doUpdate) {
			this.updateGraphItems();
		}
	}

	updateGraphItems() {
		const graph = this.viewer.getGraph();
		this.execute(SheetCommandFactory.create('command.server.UpdateGraphItemsCommand', graph));
	}

	updateShapesOnExecute(command) {
		if (command == null) return false;
		if (command.isCompoundCommand) {
			return command.commands.some((cmd) => this.updateShapesOnExecute(cmd));
		}
		// ignore commands in in-/outbox
		if (isMessageBox(command._graphItem)) return false;
		// check command
		const name = getCommandName(command);
		if (isServerCommand(name) || IGNORE_COMMANDS.includes(name)) return false;
		return TRIGGER_COMMANDS.includes(name) || isShapeSelected(this.viewer.getSelection());
	}
	updateShapesOnRedo(command) {
		return this.updateShapesOnExecute(command);
	}
	updateShapesOnUndo(command) {
		return this.updateShapesOnExecute(command);
	}
}
export default AppInteractionHandler;
